import React, { useState, useEffect } from 'react';
import { Trophy, TrendingUp, Award, Star, Users, AlertTriangle, Clock, DollarSign, Loader2, Search } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const AnalyseChauffeurs = () => {
  const [chauffeurs, setChauffeurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('score_global'); // score_global, performance, ponctualite, fiabilite, rentabilite

  useEffect(() => {
    loadChauffeurScores();
  }, []);

  const loadChauffeurScores = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ai/chauffeurs/scores?limit=100');
      // L'API retourne maintenant directement une liste
      if (Array.isArray(response.data)) {
        setChauffeurs(response.data);
      } else {
        console.error('Format de réponse inattendu:', response.data);
        toast.error('Format de données incorrect');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des scores:', error);
      toast.error('Erreur lors du chargement des analyses');
    } finally {
      setLoading(false);
    }
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'Excellent':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Très bien':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Bien':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Moyen':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      default:
        return 'bg-red-100 text-red-800 border-red-300';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-blue-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const filteredChauffeurs = chauffeurs
    .filter(ch => 
      ch.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ch.matricule.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'score_global') {
        return b.score_global - a.score_global;
      } else {
        return b.details[sortBy].score - a.details[sortBy].score;
      }
    });

  const topChauffeurs = filteredChauffeurs.slice(0, 10);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Analyse des performances en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl shadow-xl p-4 sm:p-8 mb-4 sm:mb-6 text-white">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold mb-2 flex items-center">
                <Trophy className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-3" />
                Analyse IA des Chauffeurs
              </h1>
              <p className="text-sm sm:text-base text-emerald-100">Classement basé sur performance, ponctualité, fiabilité et rentabilité</p>
            </div>
            <div className="bg-white/20 rounded-xl p-3 sm:p-4 text-center min-w-[100px]">
              <p className="text-2xl sm:text-4xl font-bold">{chauffeurs.length}</p>
              <p className="text-xs sm:text-sm text-emerald-100">Chauffeurs analysés</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 mb-4 sm:mb-6 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="score_global">Score global</option>
                <option value="performance">Performance</option>
                <option value="ponctualite">Ponctualité</option>
                <option value="fiabilite">Fiabilité</option>
                <option value="rentabilite">Rentabilité</option>
              </select>
            </div>
          </div>
        </div>

        {/* TOP 10 Podium */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-slate-200">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
            <Award className="h-6 w-6 mr-2 text-yellow-500" />
            TOP 10 Meilleurs Chauffeurs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topChauffeurs.slice(0, 3).map((chauffeur, index) => (
              <div
                key={chauffeur.chauffeur_id}
                className={`relative rounded-xl p-6 border-2 ${
                  index === 0
                    ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-400'
                    : index === 1
                    ? 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-400'
                    : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-400'
                }`}
              >
                <div className="absolute -top-4 -right-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg ${
                      index === 0
                        ? 'bg-yellow-500'
                        : index === 1
                        ? 'bg-slate-400'
                        : 'bg-orange-500'
                    }`}
                  >
                    {index + 1}
                  </div>
                </div>
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-slate-800 mb-1">{chauffeur.nom}</h3>
                  <p className="text-sm text-slate-600">{chauffeur.matricule}</p>
                </div>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600">Score Global</span>
                    <span className={`text-3xl font-bold ${getScoreColor(chauffeur.score_global)}`}>
                      {chauffeur.score_global}
                      <span className="text-lg text-slate-500">/100</span>
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        index === 0
                          ? 'bg-yellow-500'
                          : index === 1
                          ? 'bg-slate-400'
                          : 'bg-orange-500'
                      }`}
                      style={{ width: `${chauffeur.score_global}%` }}
                    />
                  </div>
                </div>
                <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${getBadgeColor(chauffeur.badge)}`}>
                  {chauffeur.badge}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-800">Classement Complet</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Rang
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Chauffeur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Badge
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <div className="flex items-center justify-center">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      Performance
                    </div>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <div className="flex items-center justify-center">
                      <Clock className="h-4 w-4 mr-1" />
                      Ponctualité
                    </div>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <div className="flex items-center justify-center">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Fiabilité
                    </div>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <div className="flex items-center justify-center">
                      <DollarSign className="h-4 w-4 mr-1" />
                      Rentabilité
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredChauffeurs.map((chauffeur, index) => (
                  <tr key={chauffeur.chauffeur_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {index < 3 && (
                          <Trophy
                            className={`h-5 w-5 mr-2 ${
                              index === 0
                                ? 'text-yellow-500'
                                : index === 1
                                ? 'text-slate-400'
                                : 'text-orange-500'
                            }`}
                          />
                        )}
                        <span className="text-sm font-medium text-slate-900">#{index + 1}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{chauffeur.nom}</div>
                        <div className="text-sm text-slate-500">{chauffeur.matricule}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getBadgeColor(chauffeur.badge)}`}>
                        {chauffeur.badge}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`text-lg font-bold ${getScoreColor(chauffeur.score_global)}`}>
                        {chauffeur.score_global}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm">
                        <div className="font-medium text-slate-900">
                          {chauffeur.details.performance.score}/{chauffeur.details.performance.max}
                        </div>
                        <div className="text-xs text-slate-500">
                          {chauffeur.details.performance.operations_completees}/{chauffeur.details.performance.operations_total} ops
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm">
                        <div className="font-medium text-slate-900">
                          {chauffeur.details.ponctualite.score}/{chauffeur.details.ponctualite.max}
                        </div>
                        <div className="text-xs text-slate-500">
                          {chauffeur.details.ponctualite.operations_a_temps} à temps
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm">
                        <div className="font-medium text-slate-900">
                          {chauffeur.details.fiabilite.score}/{chauffeur.details.fiabilite.max}
                        </div>
                        <div className="text-xs text-slate-500">
                          {chauffeur.details.fiabilite.incidents_total} incidents
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm">
                        <div className="font-medium text-slate-900">
                          {chauffeur.details.rentabilite.score}/{chauffeur.details.rentabilite.max}
                        </div>
                        <div className="text-xs text-slate-500">
                          {chauffeur.details.rentabilite.jours_immobilisation_moyen}j immo
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyseChauffeurs;
